import {
  Analytics,
  AnalyticsOutlined,
  AutoGraphOutlined,
  Logout,
  Menu as MenuIcon,
  VerifiedUser,
  VerifiedUserOutlined,
} from "@mui/icons-material";
import {
  AppBar,
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import ConfirmationDialog from "../components/dialogs/ConfirmationDialog";
import { YellowBar } from "../components/YellowBar";
import { drawerWidth } from "../config/config";
import { useDashboard } from "./hooks/useDashboard";

const GREEN = "#00C853";
const DARK = "#0D1420";

const Dashboard: React.FC = () => {
  const {
    user,
    userData,
    permittedPaths,
    isMobile,
    mobileOpen,
    dialogOpen,
    location,
    theme,
    userMenuOpen,
    userMenuAnchor,
    handleUserMenuOpen,
    handleUserMenuClose,
    setMobileOpen,
    setDialogOpen,
    handleDrawerToggle,
    handleLogout,
  } = useDashboard();

  const allMenuItems = [
    { text: "Mis Solicitudes", icon: <Analytics />, outlinedIcon: <AnalyticsOutlined />, path: "/dashboard/applications" },
    { text: "Analíticas", icon: <AutoGraphOutlined />, outlinedIcon: <AutoGraphOutlined />, path: "/dashboard/analytics" },
    { text: "Solicitudes", icon: <Analytics />, outlinedIcon: <AnalyticsOutlined />, path: "/dashboard/all-applications" },
    { text: "Usuarios", icon: <VerifiedUser />, outlinedIcon: <VerifiedUserOutlined />, path: "/dashboard/all-profile" },
    { text: "Mi Perfil", icon: <VerifiedUser />, outlinedIcon: <VerifiedUserOutlined />, path: "/dashboard/profile" },
  ];

  const visibleItems = allMenuItems.filter(({ path }) => permittedPaths.includes(path));
  const currentIndex = visibleItems.findIndex(({ path }) => location.pathname === path);

  // Iniciales del nombre
  const getInitials = (name?: string) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const menuProfile = (
    <Menu
      anchorEl={userMenuAnchor}
      open={userMenuOpen}
      onClose={handleUserMenuClose}
      PaperProps={{ sx: { mt: 1.5, minWidth: 260, borderRadius: 3, boxShadow: "0 8px 40px rgba(0,0,0,0.12)", p: 0, overflow: "hidden" } }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      {/* Header del menú */}
      <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", gap: 1.5, bgcolor: DARK, borderRadius: 0 }}>
        <Avatar sx={{ width: 44, height: 44, bgcolor: GREEN, fontWeight: 700, fontSize: 16 }}>
          {getInitials(userData?.fullName)}
        </Avatar>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#ffffff", lineHeight: 1.2 }}>
            {userData?.fullName || "Usuario"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#8899AA", mt: 0.3 }}>
            {user?.phoneNumber || user?.email}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: userData?.isVerified ? GREEN : "#f59e0b" }} />
            <Typography sx={{ fontSize: 11, color: userData?.isVerified ? GREEN : "#f59e0b", fontWeight: 600 }}>
              {userData?.isVerified ? "Verificado" : "Pendiente de verificación"}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Divider sx={{ borderColor: "#0D1420" }} />
      {/* Mi perfil */}
      <MenuItem component={Link} to="/dashboard/profile" onClick={handleUserMenuClose} sx={{ py: 1.5, px: 2.5, gap: 1.5 }}>
        <ListItemIcon sx={{ minWidth: 30, color: DARK }}><VerifiedUser fontSize="small" /></ListItemIcon>
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Mi perfil</Typography>
      </MenuItem>
      <Divider sx={{ borderColor: "#0D1420" }} />
      {/* Cerrar sesión */}
      <MenuItem onClick={() => { handleUserMenuClose(); setDialogOpen(true); }} sx={{ py: 1.5, px: 2.5, gap: 1.5, color: "error.main" }}>
        <ListItemIcon sx={{ minWidth: 30, color: "error.main" }}><Logout fontSize="small" /></ListItemIcon>
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "error.main" }}>Cerrar sesión</Typography>
      </MenuItem>
    </Menu>
  );

  // Sidebar desktop
  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: DARK }}>
      {/* Logo */}
      <Box sx={{ px: 3, py: 3, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <img src="/assets/icons/logo-dark.png" alt="PrestiYA" style={{ height: 90 }} />
      </Box>
      <Divider sx={{ borderColor: "#ffffff11" }} />
      <List sx={{ flexGrow: 1, px: 1.5, pt: 2 }}>
        {visibleItems.map(({ text, icon, path }) => {
          const isActive = location.pathname === path;
          return (
            <ListItemButton
              key={text}
              component={Link}
              to={path}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: isActive ? `${GREEN}22` : "transparent",
                borderLeft: `3px solid ${isActive ? GREEN : "transparent"}`,
                "&:hover": { bgcolor: `${GREEN}11` },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: isActive ? GREEN : "#8899AA" }}>
                {React.cloneElement(icon, { sx: { fontSize: 20 } })}
              </ListItemIcon>
              <ListItemText
                primary={text}
                slotProps={{ primary: { sx: { fontSize: 14, fontWeight: isActive ? 700 : 400, color: isActive ? GREEN : "#aabbcc" } } }}
              />
            </ListItemButton>
          );
        })}
      </List>
      {/* Usuario abajo */}
      <Box sx={{ px: 2, py: 1, borderTop: "1px solid #ffffff11" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer", p: 1, borderRadius: 2, "&:hover": { bgcolor: "#ffffff08" } }} onClick={handleUserMenuOpen}>
          <Avatar sx={{ width: 34, height: 34, bgcolor: GREEN, fontWeight: 700, fontSize: 13 }}>{getInitials(userData?.fullName)}</Avatar>
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#ffffff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{userData?.fullName || "Usuario"}</Typography>
            <Typography sx={{ fontSize: 11, color: "#8899AA", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user?.phoneNumber || user?.email}</Typography>
          </Box>
          <Logout sx={{ fontSize: 16, color: "#8899AA" }} onClick={(e) => { e.stopPropagation(); setDialogOpen(true); }} />
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <ConfirmationDialog
        open={dialogOpen}
        title="Confirmar cierre de sesión"
        message="¿Estás seguro de que deseas cerrar sesión?"
        onClose={() => setDialogOpen(false)}
        onConfirm={handleLogout}
        confirmText="Cerrar Sesión"
      />

      {/* APPBAR - solo móvil */}
      {isMobile && (
        <AppBar position="fixed" elevation={0} sx={{ bgcolor: DARK, borderBottom: `1px solid #ffffff11` }}>
          <Toolbar sx={{ justifyContent: "space-between", position: "relative" }}>
            {/* Logo PrestiYA */}
            <img src="/assets/icons/logo-dark.png" alt="PrestiYA" style={{ height: 90 }} />

            {/* Avatar derecha */}
            <Tooltip title="Cuenta">
              <Box onClick={handleUserMenuOpen} sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: GREEN, fontWeight: 700, fontSize: 13 }}>{getInitials(userData?.fullName)}</Avatar>
              </Box>
            </Tooltip>
          </Toolbar>
        </AppBar>
      )}

      {menuProfile}

      {/* SIDEBAR - solo desktop */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": { width: drawerWidth, boxSizing: "border-box", bgcolor: DARK, border: "none" },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* CONTENIDO PRINCIPAL */}
      <Box component="main" sx={{ flexGrow: 1, bgcolor: "background.default", pb: isMobile ? 8 : 0 }}>
        {isMobile && <Toolbar sx={{ minHeight: "90px !important" }} />}
        <YellowBar />
        <Box sx={{ p: 2 }}>
          <Outlet />
        </Box>
      </Box>

      {/* BOTTOM NAVIGATION - solo móvil */}
      {isMobile && (
        <Paper sx={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100, borderTop: "1px solid #f0f0f0" }} elevation={3}>
          <BottomNavigation
            value={currentIndex}
            showLabels
            sx={{ bgcolor: "#ffffff", "& .Mui-selected": { color: GREEN } }}
          >
            {visibleItems.map(({ text, icon, outlinedIcon, path }, idx) => (
              <BottomNavigationAction
                key={text}
                label={text}
                icon={currentIndex === idx ? icon : outlinedIcon}
                component={Link}
                to={path}
                sx={{
                  color: "#8899AA",
                  "&.Mui-selected": { color: GREEN },
                  fontSize: 10,
                  minWidth: 0,
                  "& .MuiBottomNavigationAction-label": { fontSize: 10, opacity: 1 },
                }}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
};

export default Dashboard;

